import { LiveAnnouncer } from '@angular/cdk/a11y';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, forwardRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { startWith, tap } from 'rxjs';
import { Tag } from './tag.type';

@Component({
    selector: 'tm-tags-input',
    templateUrl: './tags-input.component.html',
    styleUrls: ['./tags-input.component.scss'],
    imports: [
        MatAutocompleteModule,
        MatChipsModule,
        MatIconModule,
        MatFormFieldModule,
        MatOptionModule,
        ReactiveFormsModule,
        TranslateModule
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TmTagsInputComponent),
            multi: true
        },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        class: 'tm-tags-input'
    }
})
export class TmTagsInputComponent implements ControlValueAccessor {
  private _tags: string[] = [];
  private _allTags: string[] = [];
  protected filteredTags: string[];
  protected isDisabled: boolean;
  protected separatorKeysCodes: number[] = [ENTER, COMMA];

  @Input({ required: true })
  set allTags(value: string[]) {
    this._allTags = value;
    this._propagateOnFilteredTags();
  }
  
  get allTags() {
    return this._allTags;
  }
  
  @Output()
  tagAdded = new EventEmitter<string>();
  
  @Output()
  tagRemoved = new EventEmitter<string>();
  
  protected get tags() {
    return this._tags;
  }

  protected set tags(value) {
    this._tags = value;
    const tags = value.map(tag => ({ name: tag }));
    this.onChange(tags);
    this.onTouched();
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  protected tagFilterCtrl = new FormControl<string>('');

  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;
  
  announcer = inject(LiveAnnouncer);

  constructor() {
    this.tagFilterCtrl.valueChanges.pipe(
      startWith(''),
      tap(() => this._propagateOnFilteredTags()),
    ).subscribe();
  }
  
  writeValue(value: Tag[]): void {
    this._tags = value?.map(tag => tag.name) || [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    // Add tag
    if (value) {
      this.tags = [...this.tags, value];

      this.announcer.announce(`Added ${value}`);
      this.tagAdded.emit(value);
    }

    // Clear the input value
    event.chipInput!.clear();
    this.tagFilterCtrl.setValue('');
  }

  remove(tag: string): void {
    const index = this.tags.indexOf(tag);

    if (index >= 0) {
      this.tags = this.tags.filter((_, i) => i !== index);

      this.announcer.announce(`Removed ${tag}`);
      this.tagRemoved.emit(tag);
    }

    this._propagateOnFilteredTags();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.viewValue;
    this.tags = [...this.tags, value];
    this.tagInput.nativeElement.value = '';
    this.tagAdded.emit(value);
    this.tagFilterCtrl.setValue('');
  }

  private _propagateOnFilteredTags(): void {
    const tagFilter = this.tagFilterCtrl.value;
    this.filteredTags = this._filter(tagFilter);
  }

  private _filter(value: string | null): string[] {
    const filterValue = value.toLowerCase();

    return this.allTags?.filter(tag => tag?.toLowerCase().includes(filterValue) && !this.tags.includes(tag)) || [];
  }
}
